/* eslint-disable unicode-bom */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

function SafetyReportApprovalListModal({ show, onClose, project, approvals = [], onPrintSingle, onPrintAll, isLoading = false }) {
  const [checkedItems, setCheckedItems] = useState(new Set());

  useEffect(() => {
    if (show && approvals.length > 0) {
      setCheckedItems(new Set(approvals.map((t, idx) => t.id || t.aprv_id || idx)));
    }
  }, [show, approvals]);

  if (!show || !project) return null;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setCheckedItems(new Set(approvals.map((t, idx) => t.id || t.aprv_id || idx)));
    } else {
      setCheckedItems(new Set());
    }
  };

  const handleSelectItem = (idOrIdx, checked) => {
    const newChecked = new Set(checkedItems);
    if (checked) {
      newChecked.add(idOrIdx);
    } else {
      newChecked.delete(idOrIdx);
    }
    setCheckedItems(newChecked);
  };

  const handlePrintSelected = () => {
    const selectedDocs = approvals.filter((t, idx) => checkedItems.has(t.id || t.aprv_id || idx));
    onPrintAll(selectedDocs);
  };

  return createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 999998,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '850px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', maxHeight: '90vh', position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        {isLoading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.9)', zIndex: 9999,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
          }}>
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
            <h5 className="fw-bold text-dark">데이터를 로드 중입니다...</h5>
          </div>
        )}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
          <h4 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1e293b' }}>
            <i className="fas fa-file-signature me-2 text-primary"></i>
            전자결재 출력 목록
          </h4>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#64748b', cursor: 'pointer', padding: 0, lineHeight: 1 }}>&times;</button>
        </div>

        <div style={{ padding: '24px', overflowY: isLoading ? 'hidden' : 'auto', flex: 1, minHeight: 0, maxHeight: '450px' }}>
          <div style={{ marginBottom: '16px', color: '#475569', fontSize: '0.95rem' }}>
            <strong>[{project.name || '프로젝트'}]</strong>에 등록된 결재완료 기안문 목록입니다. 인쇄할 항목을 선택하세요.
          </div>

          {approvals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>등록된 결재완료 문서가 없습니다.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', borderCollapse: 'separate', borderSpacing: 0, overflow: 'hidden' }}>
                <thead className="table-light" style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>
                  <tr>
                    <th className="text-center border-bottom-0" style={{ width: '40px', padding: '12px' }}>
                      <input 
                        type="checkbox" 
                        style={{ cursor: 'pointer' }}
                        checked={approvals.length > 0 && checkedItems.size === approvals.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-center border-bottom-0" style={{ width: '50px', padding: '12px' }}>No</th>
                    <th className="border-bottom-0" style={{ width: '150px', padding: '12px' }}>기안 일자</th>
                    <th className="border-bottom-0" style={{ padding: '12px' }}>결재 제목</th>
                    <th className="text-center border-bottom-0" style={{ width: '150px', padding: '12px' }}>기안자</th>
                    <th className="text-center border-bottom-0" style={{ width: '90px', padding: '12px' }}>출력</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '0.8rem' }}>
                  {approvals.map((t, idx) => {
                    const idOrIdx = t.id || t.aprv_id || idx;
                    const isChecked = checkedItems.has(idOrIdx);
                    
                    const title = t.doc_title || t.title || '(제목 없음)';
                    const drafter = (t.lines && t.lines.length > 0) ? `${t.lines[0].user_name} ${t.lines[0].user_position || ''}` : '-';
                    const dateStr = t.make_date || t.doc_date || t.created_at || t.draftDate || t.updated_at || '';
                    
                    let displayDate = '-';
                    if (dateStr) {
                      const d = new Date(dateStr);
                      if (!isNaN(d.getTime())) {
                        displayDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      }
                    }

                    return (
                      <tr key={idOrIdx} style={{ backgroundColor: isChecked ? '#f0f9ff' : 'transparent', transition: 'background-color 0.2s' }}>
                        <td className="text-center" style={{ padding: '12px' }}>
                          <input 
                            type="checkbox" 
                            style={{ cursor: 'pointer' }}
                            checked={isChecked}
                            onChange={(e) => handleSelectItem(idOrIdx, e.target.checked)}
                          />
                        </td>
                        <td className="text-center text-muted" style={{ padding: '12px' }}>{idx + 1}</td>
                        <td style={{ padding: '12px' }}>
                          <div>{displayDate}</div>
                        </td>
                        <td style={{ fontWeight: 600, padding: '12px', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={title}>
                          {title}
                        </td>
                        <td className="text-center" style={{ padding: '12px', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={drafter}>
                          {drafter}
                        </td>
                        <td className="text-center" style={{ padding: '12px' }}>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            style={{ borderRadius: '6px', fontWeight: 600, padding: '4px 12px' }}
                            onClick={() => onPrintSingle(t)}
                          >
                            <i className="fas fa-print me-1"></i>인쇄
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8fafc' }}>
          <button className="btn btn-secondary" style={{ borderRadius: '8px', fontWeight: 600, padding: '8px 20px' }} onClick={onClose}>닫기</button>
          {approvals.length > 0 && (
            <button 
              className="btn btn-primary" 
              style={{ borderRadius: '8px', fontWeight: 600, padding: '8px 20px' }} 
              onClick={handlePrintSelected}
              disabled={checkedItems.size === 0}
            >
              <i className="fas fa-print me-2"></i>선택 연속 인쇄 {checkedItems.size > 0 ? `(${checkedItems.size}건)` : ''}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SafetyReportApprovalListModal;
